// src/components/onboarding/OnboardingModal.jsx
//
// Post-login welcome flow, restyled to match the standalone
// tutorial-staff.html layout: single-column centred main area,
// fixed 64px header (KweryAI · step pills · counter+close),
// fixed 64px footer (Back · dots · Next/primary).
//
// Modes:
//   fullScreen={true}   — used by /onboarding route (default in app)
//   fullScreen={false}  — overlay mode; same layout, sits on a dim backdrop
//
// Persistence:
//   kwery_onboarding_step_<uid>  — last step (resume on close)
//   kwery_onboarding_done_<uid>  — set on final-step finish; helper
//                                  short-circuits past /onboarding after this

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  X, ArrowRight, Check, AlertTriangle,
  Calendar, Wallet, FileText, Clock, GraduationCap, Ticket,
  Mail, Play,
} from "lucide-react";
import { toast } from "../ui/Toast";

const API = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const authH = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  "Content-Type": "application/json",
});

const TOTAL_STEPS = 7;
const STEP_LABELS = [
  "Welcome", "Buddy", "Tour", "Tasks", "Policies", "KIRA", "Done",
];

// ─────────────────────────────────────────────────────────────
//  Role-personalised copy
// ─────────────────────────────────────────────────────────────
// Role-specific intro copy. Each entry drives the "welcome" subhead
// (step 1) and the KIRA intro (step 2). Steps 3-7 are shared but the
// renderer also branches on roleKey for headcount / approvals language.
const ROLE_MESSAGES = {
  staff: {
    sub: (dept, org) =>
      `You've just joined ${dept || "the team"}${org ? " at " + org : ""}. ` +
      "We're excited to have you. Let's get you set up in the next few minutes.",
    kira: (first) =>
      `Hi ${first}! I'm KIRA, your AI assistant. I can help you check your leave balance, ` +
      "find policies, query your payslips, or just answer HR questions — anytime, in plain English.",
    perks: "You can check your leave balance, view payslips, raise tickets and more.",
  },
  manager: {
    sub: (dept, _org, title) =>
      `You're joining as ${title || "a manager"} in ${dept || "the team"}. ` +
      "Your team is ready and waiting. Let's get your manager toolkit set up.",
    kira: (first) =>
      `Hi ${first}! I'm KIRA. Ask me "Give me a briefing on my team" and I'll ` +
      "summarise attendance, leave, performance and pending approvals in seconds.",
    perks: "You have access to team management, approvals, and performance tools.",
  },
  admin: {
    sub: (_dept, org) =>
      `You manage ${org || "your organisation"} on KweryAI. ` +
      "Use the admin panel for users, payroll, policies and modules.",
    kira: (first) =>
      `Hi ${first}! I'm KIRA. Ask me about compliance, data quality, attrition trends ` +
      "or payroll costs and I'll surface the answer instantly.",
    perks: "You have admin access to manage your organisation's HR platform.",
  },
  executive: {
    sub: (_dept, org) =>
      `Your executive dashboard shows org-wide KPIs for ${org || "your organisation"} — ` +
      "headcount, payroll cost, attrition rate and open roles in one view.",
    kira: (first) =>
      `Hi ${first}! I'm KIRA. Try "Give me an executive briefing" for an instant ` +
      "org-wide summary the moment you log in.",
    perks: "You have executive access to org-wide analytics and insights.",
  },
};

const ROLE_TO_TUTORIAL = {
  staff:      "/tutorial-staff.html",
  manager:    "/tutorial-manager.html",
  admin:      "/tutorial-manager.html",
  executive:  "/tutorial-executive.html",
  superadmin: "/tutorial-executive.html",
};

const KIRA_CAPABILITIES = [
  { icon: Calendar,      title: "Leave",      example: "How many leave days do I have left?",  prefill: "How many leave days do I have left?" },
  { icon: Wallet,        title: "Payslips",   example: "Show me my last payslip",              prefill: "Show me my last payslip" },
  { icon: FileText,      title: "Policies",   example: "What's the remote work policy?",        prefill: "What's the remote work policy?" },
  { icon: Clock,         title: "Attendance", example: "Am I on time this week?",               prefill: "Am I on time this week?" },
  { icon: GraduationCap, title: "Learning",   example: "What courses should I do?",             prefill: "What courses should I do?" },
  { icon: Ticket,        title: "Tickets",    example: "Raise an IT support ticket",            prefill: "Raise an IT support ticket" },
];

// Each role now has dedicated copy — executives no longer get folded
// into the admin track (used to before the platform-intro work).
const ROLE_KEY = (role) =>
  ROLE_MESSAGES[role] ? role : "staff";

const initials = (name) =>
  (name || "?")
    .split(/\s+/).filter(Boolean).slice(0, 2)
    .map((p) => p[0].toUpperCase()).join("") || "?";

// ─────────────────────────────────────────────────────────────
//  Component
// ─────────────────────────────────────────────────────────────
export default function OnboardingModal({ onClose, onComplete, fullScreen = false }) {
  const navigate = useNavigate();

  const user = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("user") || "null"); } catch { return null; }
  }, []);
  const userId       = user?.id || "anon";
  const STORAGE_KEY  = `kwery_onboarding_step_${userId}`;
  const COMPLETE_KEY = `kwery_onboarding_done_${userId}`;

  const [step, setStep] = useState(() => {
    try {
      const saved = parseInt(localStorage.getItem(STORAGE_KEY) || "1", 10);
      return Number.isFinite(saved) && saved >= 1 && saved <= TOTAL_STEPS ? saved : 1;
    } catch { return 1; }
  });
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, String(step)); } catch {}
  }, [step, STORAGE_KEY]);

  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches
  );
  useEffect(() => {
    const mql = window.matchMedia("(max-width: 768px)");
    const handler = (e) => setIsMobile(e.matches);
    mql.addEventListener?.("change", handler);
    return () => mql.removeEventListener?.("change", handler);
  }, []);

  // ── Data fetched on mount ──
  const [profile,    setProfile]    = useState(null);
  const [onboarding, setOnboarding] = useState(null);
  const [tasks,      setTasks]      = useState([]);
  const [policies,   setPolicies]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);

  const [readingPolicy, setReadingPolicy] = useState(null);
  const [ackChecked,    setAckChecked]    = useState(false);
  // Renamed from `toast` to avoid shadowing the imported `toast` helper
  // from "../ui/Toast" — the shadowing meant toast.error(...) was being
  // read off this state (initially null) and threw
  // "Cannot read properties of null (reading 'error')" inside completeAll.
  const [dismissToast,  setDismissToast]  = useState(null);
  const [checkmarkPulse, setCheckmarkPulse] = useState(false);

  // ── TRIGGER hook ──
  // The moment onboarding is SHOWN, durably flip the server-side
  // `onboarding_started` routing gate (before the user finishes). This is
  // what guarantees an abandoned tour never re-routes the user back into
  // onboarding on their next login. Fire-once, best-effort — a failure here
  // is non-blocking (the completion hook also sets the flag as a safety net).
  useEffect(() => {
    fetch(`${API}/api/lifecycle/onboarding/started`, {
      method: "PATCH", headers: authH(),
    }).catch(() => {
      // Non-fatal — never block the onboarding UI on this write.
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [pRes, obRes, polRes] = await Promise.all([
          fetch(`${API}/api/profile/me`,                           { headers: authH() }).then(r => r.json()).catch(() => null),
          fetch(`${API}/api/lifecycle/onboarding/my-tasks`,         { headers: authH() }).then(r => r.json()).catch(() => null),
          fetch(`${API}/api/lifecycle/onboarding/policies`,         { headers: authH() }).then(r => r.json()).catch(() => null),
        ]);
        if (cancelled) return;
        setProfile(pRes?.data || pRes || null);
        setOnboarding(obRes?.onboarding || null);
        setTasks(Array.isArray(obRes?.tasks) ? obRes.tasks : []);
        setPolicies(Array.isArray(polRes?.policies) ? polRes.policies : []);
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load onboarding data");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // Pulse the check on step 7 mount
  useEffect(() => {
    if (step === 7) {
      setCheckmarkPulse(true);
      const t = setTimeout(() => setCheckmarkPulse(false), 600);
      return () => clearTimeout(t);
    }
  }, [step]);

  // ── Derived ──
  const first      = (profile?.name || user?.full_name || user?.email || "there").split(/\s+/)[0];
  const dept       = profile?.department || "";
  const orgName    = profile?.org_name || profile?.organisation_name || "";
  const title      = profile?.title || "";
  const roleKey    = ROLE_KEY((user?.role || profile?.role || "staff").toLowerCase());
  const copy       = ROLE_MESSAGES[roleKey];

  const completedN = tasks.filter((t) => t.completed).length;
  const totalN     = tasks.length;
  const taskPct    = totalN ? Math.round((completedN / totalN) * 100) : 0;

  const requiredAcks = policies.filter((p) => p.requires_ack);
  const ackedCount   = requiredAcks.filter((p) => p.acknowledged).length;

  const buddy = onboarding?.buddy_info || (onboarding?.buddy_name ? { name: onboarding.buddy_name } : null);

  // ── Actions ──
  const goNext = useCallback(() => setStep((s) => Math.min(TOTAL_STEPS, s + 1)), []);
  const goBack = useCallback(() => setStep((s) => Math.max(1, s - 1)), []);
  const goTo   = useCallback((n) => setStep(Math.max(1, Math.min(TOTAL_STEPS, n))), []);

  const handleClose = () => {
    try { localStorage.setItem(STORAGE_KEY, String(step)); } catch {}
    setDismissToast({ text: "We'll remind you to finish your onboarding." });
    setTimeout(() => { onClose?.(); }, 900);
  };

  const completeTask = async (taskId) => {
    setTasks((rows) => rows.map((t) => t.task_id === taskId
      ? { ...t, completed: true, completed_at: new Date().toISOString() } : t));
    try {
      const r = await fetch(`${API}/api/lifecycle/onboarding/tasks/${taskId}/complete`, {
        method: "POST", headers: authH(), body: JSON.stringify({}),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success("Task completed ✓");
    } catch {
      setTasks((rows) => rows.map((t) => t.task_id === taskId
        ? { ...t, completed: false, completed_at: null } : t));
      toast.error("Task save failed — reverted");
    }
  };

  const openPolicy  = (p) => { setAckChecked(false); setReadingPolicy(p); };
  const closePolicy = ()  => { setReadingPolicy(null); setAckChecked(false); };

  const acknowledgePolicy = async () => {
    if (!readingPolicy) return;
    const pid = readingPolicy.id;
    try {
      const r = await fetch(`${API}/api/admin/policies/${pid}/acknowledge`, {
        method: "POST", headers: authH(),
      });
      if (!r.ok) throw new Error("ack failed");
      setPolicies((rows) => rows.map((x) => x.id === pid ? { ...x, acknowledged: true } : x));
      toast.success("Policy acknowledged ✓");
    } catch {
      setError("Could not acknowledge — please try again.");
      setTimeout(() => setError(null), 4000);
      toast.error("Failed to acknowledge");
    } finally {
      closePolicy();
    }
  };

  const handleCapabilityClick = (cap) => {
    try { localStorage.setItem("kwery_kira_prefill", cap.prefill); } catch {}
    try {
      window.dispatchEvent(new CustomEvent("kira_prefill_request", { detail: { text: cap.prefill } }));
    } catch {}
    completeAll();
    navigate("/messenger/dashboard");
  };

  const handleOpenTutorial = () => {
    const url = ROLE_TO_TUTORIAL[roleKey === "admin" ? "manager" : roleKey] || ROLE_TO_TUTORIAL.staff;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const completeAll = async () => {
    try { localStorage.setItem(COMPLETE_KEY, "true"); } catch {}
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
    try {
      const r = await fetch(`${API}/api/lifecycle/onboarding/complete`, {
        method: "PATCH", headers: authH(),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success("Onboarding complete — welcome aboard!");
    } catch {
      // Cosmetic failure — still proceed with navigation so the user can
      // get into the app.
      toast.error("Could not finalise onboarding");
    }
    onComplete?.();
  };

  const handleFinish = async () => {
    await completeAll();
    // First-login path: onboarding → KIRA welcome → dashboard. `completeAll`
    // also fires onComplete (which navigates to /welcome); navigating here too
    // keeps the destination correct even if no onComplete was supplied.
    navigate("/welcome");
  };

  // ─────────────────────────────────────────────────────────
  //  Styles
  // ─────────────────────────────────────────────────────────
  const NEON     = "#00FF99";
  const NEON_BG  = "rgba(0,255,153,0.08)";
  const NEON_BG2 = "rgba(0,255,153,0.15)";
  const NEON_BDR = "rgba(0,255,153,0.2)";
  const NEON_BDR2= "rgba(0,255,153,0.4)";

  const shell = fullScreen
    ? { position: "fixed", inset: 0, zIndex: 9999,
        background: "#080808", color: "#fff",
        display: "flex", flexDirection: "column" }
    : { position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(0,0,0,0.92)", backdropFilter: "blur(4px)",
        display: "flex", alignItems: "center", justifyContent: "center" };

  const inner = fullScreen
    ? { width: "100%", height: "100%", display: "flex", flexDirection: "column" }
    : { width: "min(960px, 95vw)", height: "min(720px, 90vh)",
        background: "#080808", color: "#fff",
        borderRadius: 16, overflow: "hidden",
        border: `0.5px solid #1a1a1a`,
        display: "flex", flexDirection: "column" };

  const headerBar = {
    height: 64, flexShrink: 0,
    background: "#050505", borderBottom: "1px solid #111",
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: isMobile ? "0 16px" : "0 48px",
  };
  const brand = { color: NEON, fontSize: 20, fontWeight: 600, letterSpacing: 0.4 };
  const footerBar = {
    height: 64, flexShrink: 0,
    background: "#050505", borderTop: "1px solid #111",
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: isMobile ? "0 16px" : "0 48px",
  };
  const main = {
    flex: 1, overflowY: "auto",
    display: "flex", alignItems: "center", justifyContent: "center",
    position: "relative",
  };
  const innerMain = {
    width: "100%", maxWidth: 780,
    padding: isMobile ? "40px 24px" : "60px 48px",
    margin: "0 auto",
  };

  // Keyframes (only the ones still in use)
  const keyframes = `
    @keyframes kwery-pulse    { 0% { transform: scale(0); opacity: 0; } 60% { transform: scale(1.2); opacity: 1; } 100% { transform: scale(1); opacity: 1; } }
    @keyframes kwery-tick     { 0% { transform: scale(1); } 50% { transform: scale(1.3); } 100% { transform: scale(1); } }
    @keyframes kwery-slide-up { from { transform: translateY(100%); } to { transform: translateY(0); } }
  `;

  // ─────────────────────────────────────────────────────────
  //  Sub-components
  // ─────────────────────────────────────────────────────────
  const StepPills = () => {
    if (isMobile) {
      return (
        <div style={{ color: "#333", fontSize: 13 }}>
          Step {step} of {TOTAL_STEPS}
        </div>
      );
    }
    return (
      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "nowrap" }}>
        {STEP_LABELS.map((label, i) => {
          const num = i + 1;
          const active   = num === step;
          const complete = num < step;
          let style;
          if (complete) {
            style = {
              background: NEON_BG, color: NEON, border: "0.5px solid transparent",
            };
          } else if (active) {
            style = {
              background: NEON_BG2, color: NEON,
              border: `0.5px solid ${NEON_BDR2}`, fontWeight: 500,
            };
          } else {
            style = { color: "#2a2a2a", border: "0.5px solid transparent" };
          }
          return (
            <span key={label} style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "4px 12px", borderRadius: 20, fontSize: 12,
              whiteSpace: "nowrap", transition: "all 0.2s",
              ...style,
            }}>
              {complete ? "✓ " : ""}{label}
            </span>
          );
        })}
      </div>
    );
  };

  const Eyebrow = () => (
    <div style={{
      fontSize: 13, color: "#333", letterSpacing: 3,
      textTransform: "uppercase", marginBottom: 20,
    }}>Step {step} of {TOTAL_STEPS}</div>
  );

  const headlineH1 = {
    fontSize: 42, fontWeight: 500, lineHeight: 1.2,
    color: "#fff", margin: "0 0 12px",
  };
  const subText = {
    fontSize: 18, color: "#666", lineHeight: 1.7,
    margin: "0 0 40px", maxWidth: 620,
  };
  const innerCard = {
    background: "#0a0a0a", border: "0.5px solid #1a1a1a",
    borderRadius: 14, padding: 28,
  };
  const taskCardBase = {
    background: "#0a0a0a", border: "0.5px solid #111",
    borderRadius: 10, padding: "16px 20px", marginBottom: 8,
    display: "flex", alignItems: "center", gap: 16,
  };

  // ─────────────────────────────────────────────────────────
  //  STEP 1 — Welcome
  // ─────────────────────────────────────────────────────────
  const renderStep1 = () => (
    <div>
      <Eyebrow />
      <h1 style={headlineH1}>
        Welcome to <span style={{ color: NEON }}>KweryAI</span>, {first}
      </h1>
      <p style={subText}>{copy.sub(dept, orgName, title)}</p>
      <div style={{
        background: "#0a0a0a",
        border: `0.5px solid ${NEON_BDR}`,
        borderRadius: 14, borderBottomLeftRadius: 2,
        padding: "20px 24px", maxWidth: 580,
      }}>
        <div style={{ fontSize: 10, color: NEON, letterSpacing: 3, marginBottom: 8, fontWeight: 600 }}>K·I·R·A</div>
        <div style={{ fontSize: 16, color: "#888", lineHeight: 1.7 }}>{copy.kira(first)}</div>
      </div>
    </div>
  );

  // ─────────────────────────────────────────────────────────
  //  STEP 2 — Buddy
  // ─────────────────────────────────────────────────────────
  const renderStep2 = () => (
    <div>
      <Eyebrow />
      <h1 style={headlineH1}>Meet your <span style={{ color: NEON }}>buddy</span></h1>
      <p style={subText}>The person who'll show you the ropes.</p>

      {buddy ? (
        <div style={{ ...innerCard, maxWidth: 640, padding: "28px 32px", position: "relative" }}>
          <div style={{ display: "flex", gap: 18, alignItems: "center" }}>
            <div style={{
              width: 68, height: 68, borderRadius: "50%",
              background: "linear-gradient(135deg, rgba(0,255,153,0.3), rgba(0,255,153,0.05))",
              border: `1px solid ${NEON_BDR}`,
              fontSize: 22, fontWeight: 500, color: NEON,
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}>{initials(buddy.name)}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 22, color: "#fff", fontWeight: 500 }}>{buddy.name}</div>
              <div style={{ fontSize: 14, color: "#555", marginTop: 4 }}>
                {[buddy.job_title, buddy.department].filter(Boolean).join(" · ") || "Buddy"}
              </div>
              <div style={{
                display: "inline-block", marginTop: 10,
                background: NEON_BG, border: `0.5px solid ${NEON_BDR}`,
                color: NEON, fontSize: 11, padding: "3px 11px",
                borderRadius: 20, letterSpacing: 0.5,
              }}>Your buddy for day 1–30</div>
            </div>
          </div>

          <div style={{ height: "0.5px", background: "#111", margin: "20px 0" }} />

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
            <BuddyStat label="Department" value={buddy.department || "—"} />
            <BuddyStat label="Role"       value={buddy.job_title || "—"} />
            <BuddyStat label="Email"      value={buddy.email || "—"} truncate />
          </div>

          {buddy.email && (
            <button
              type="button"
              onClick={() => { try { navigator.clipboard.writeText(buddy.email); } catch {} }}
              style={{
                marginTop: 20, width: "100%", height: 44,
                background: "transparent",
                border: "0.5px solid #1a1a1a",
                borderRadius: 8, color: "#555",
                fontSize: 14, cursor: "pointer",
                display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
                transition: "all 0.15s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = NEON; e.currentTarget.style.color = NEON; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#1a1a1a"; e.currentTarget.style.color = "#555"; }}
              title={`Copy ${buddy.email}`}
            >
              <Mail size={14} /> Message {buddy.name.split(/\s+/)[0]}
            </button>
          )}
        </div>
      ) : (
        <div style={{ ...innerCard, maxWidth: 640, color: "#666", fontSize: 15, lineHeight: 1.7 }}>
          Your buddy is being assigned — your manager will introduce you shortly.
          In the meantime, KIRA can answer any question you've got.
        </div>
      )}
    </div>
  );

  // ─────────────────────────────────────────────────────────
  //  STEP 3 — Tutorial
  // ─────────────────────────────────────────────────────────
  const renderStep3 = () => (
    <div>
      <Eyebrow />
      <h1 style={headlineH1}>Your platform <span style={{ color: NEON }}>walkthrough</span></h1>
      <p style={subText}>A quick tour of everything KweryAI can do for you.</p>

      <div
        role="button"
        onClick={handleOpenTutorial}
        style={{
          ...innerCard, maxWidth: 640, padding: 32,
          display: "flex", alignItems: "center", gap: 24,
          cursor: "pointer", transition: "border-color 0.15s",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = "rgba(0,255,153,0.3)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#1a1a1a"; }}
      >
        <div style={{
          width: 72, height: 72, borderRadius: "50%",
          background: NEON_BG, border: `1px solid ${NEON_BDR}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: NEON, fontSize: 24, flexShrink: 0,
        }}>
          <Play size={26} fill={NEON} />
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 18, color: "#fff", fontWeight: 500 }}>Platform Walkthrough</div>
          <div style={{ fontSize: 14, color: "#555", marginTop: 6 }}>
            Opens your personalised tour in a new tab · 5 min
          </div>
        </div>
      </div>
    </div>
  );

  // ─────────────────────────────────────────────────────────
  //  STEP 4 — Tasks
  // ─────────────────────────────────────────────────────────
  const renderStep4 = () => (
    <div>
      <Eyebrow />
      <h1 style={headlineH1}>Your onboarding <span style={{ color: NEON }}>checklist</span></h1>
      <p style={subText}>Complete these in your first week.</p>

      <div style={{ height: 4, background: "#111", borderRadius: 2, marginBottom: 8 }}>
        <div style={{
          height: "100%", background: NEON,
          width: `${taskPct}%`, borderRadius: 2, transition: "width 0.5s ease",
        }} />
      </div>
      <div style={{ fontSize: 13, color: "#444", textAlign: "right", marginBottom: 24 }}>
        {completedN} of {totalN} tasks complete
      </div>

      {tasks.length === 0 ? (
        <p style={{ ...subText, fontSize: 14, fontStyle: "italic" }}>No tasks assigned yet.</p>
      ) : tasks.map((t) => {
        const isStaff   = (t.assigned_to_role || "staff") === "staff";
        const isOverdue = !t.completed && t.due_date && new Date(t.due_date) < new Date();
        return (
          <div
            key={t.task_id}
            onClick={() => isStaff && !t.completed && completeTask(t.task_id)}
            style={{
              ...taskCardBase,
              cursor: isStaff && !t.completed ? "pointer" : "default",
              opacity: t.completed ? 0.55 : (isStaff ? 1 : 0.65),
              transition: "all 0.15s",
            }}
            onMouseEnter={(e) => {
              if (isStaff && !t.completed) {
                e.currentTarget.style.borderColor = "#1e1e1e";
                e.currentTarget.style.background  = "#0d0d0d";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "#111";
              e.currentTarget.style.background  = "#0a0a0a";
            }}
          >
            <div style={{
              width: 24, height: 24, borderRadius: "50%", flexShrink: 0,
              border: t.completed ? "none" : "1.5px solid #222",
              background: t.completed ? NEON : "transparent",
              display: "flex", alignItems: "center", justifyContent: "center",
              animation: t.completed ? "kwery-tick 0.3s ease-out" : undefined,
            }}>
              {t.completed && <Check size={12} color="#030303" strokeWidth={3} />}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: 15, color: t.completed ? "#333" : "#ccc",
                textDecoration: t.completed ? "line-through" : "none",
              }}>{t.title}</div>
              {!isStaff && (
                <div style={{ fontSize: 12, color: "#444", marginTop: 2 }}>
                  · assigned to {t.assigned_to_role}
                </div>
              )}
            </div>
            {t.due_date && (
              <div style={{
                fontSize: 11, padding: "3px 10px", borderRadius: 20,
                background: isOverdue ? "rgba(226,75,74,0.1)" : "#111",
                color:      isOverdue ? "#E24B4A" : "#333",
                whiteSpace: "nowrap",
              }}>
                {isOverdue ? "Overdue" : `Due ${t.due_date}`}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  // ─────────────────────────────────────────────────────────
  //  STEP 5 — Policies
  // ─────────────────────────────────────────────────────────
  const renderStep5 = () => (
    <div>
      <Eyebrow />
      <h1 style={headlineH1}>Before you <span style={{ color: NEON }}>start</span></h1>
      <p style={subText}>These policies shape how we work together.</p>

      {policies.length === 0 ? (
        <p style={{ ...subText, fontSize: 14, fontStyle: "italic" }}>
          No published policies yet — HR will upload these shortly.
        </p>
      ) : policies.slice(0, 5).map((p) => (
        <div key={p.id} style={{
          ...taskCardBase, padding: "18px 20px",
        }}>
          <div style={{
            width: 40, height: 40, borderRadius: "50%",
            background: "#0f0f0f", border: "0.5px solid #1a1a1a",
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}>
            <FileText size={16} color={NEON} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, color: "#ccc" }}>{p.title}</div>
            <div style={{ fontSize: 12, color: "#444", marginTop: 2 }}>{p.category || "Policy"}</div>
          </div>
          {p.acknowledged ? (
            <span style={{ fontSize: 13, color: NEON, display: "inline-flex", alignItems: "center", gap: 4 }}>
              <Check size={13} /> Read
            </span>
          ) : (
            <button
              type="button"
              onClick={() => openPolicy(p)}
              style={{
                fontSize: 13, color: NEON,
                background: NEON_BG, border: `0.5px solid ${NEON_BDR}`,
                padding: "7px 14px", borderRadius: 8,
                cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6,
              }}
            >
              Read <ArrowRight size={12} />
            </button>
          )}
        </div>
      ))}

      {requiredAcks.length > 0 && (
        <p style={{ fontSize: 13, color: "#444", marginTop: 16 }}>
          {ackedCount} of {requiredAcks.length} acknowledged
        </p>
      )}
    </div>
  );

  // ─────────────────────────────────────────────────────────
  //  STEP 6 — KIRA tour
  // ─────────────────────────────────────────────────────────
  const renderStep6 = () => (
    <div>
      <Eyebrow />
      <h1 style={headlineH1}>Ask <span style={{ color: NEON }}>KIRA</span> anything</h1>
      <p style={subText}>Here's what your AI assistant can help you with.</p>

      <div style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
        gap: 10,
      }}>
        {KIRA_CAPABILITIES.map((c) => {
          const Icon = c.icon;
          return (
            <button
              key={c.title}
              onClick={() => handleCapabilityClick(c)}
              style={{
                background: "#0a0a0a", border: "0.5px solid #111",
                borderRadius: 10, padding: "18px 20px",
                cursor: "pointer", textAlign: "left", color: "#fff",
                transition: "all 0.15s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = NEON_BDR; e.currentTarget.style.background = "#0d0d0d"; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#111";    e.currentTarget.style.background = "#0a0a0a"; }}
            >
              <Icon size={20} color={NEON} />
              <div style={{ fontSize: 14, color: "#fff", marginTop: 8, fontWeight: 500 }}>{c.title}</div>
              <div style={{ fontSize: 13, color: "#444", marginTop: 4 }}>{c.example}</div>
            </button>
          );
        })}
      </div>
    </div>
  );

  // ─────────────────────────────────────────────────────────
  //  STEP 7 — All set
  // ─────────────────────────────────────────────────────────
  const renderStep7 = () => (
    <div style={{ textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center" }}>
      <div style={{
        width: 96, height: 96, borderRadius: "50%",
        background: NEON_BG, border: `1px solid ${NEON_BDR}`,
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        fontSize: 44, color: NEON,
        animation: checkmarkPulse ? "kwery-pulse 0.5s ease-out" : undefined,
      }}>✓</div>

      <h1 style={{ ...headlineH1, marginTop: 24, textAlign: "center" }}>
        You're all <span style={{ color: NEON }}>set</span>, {first}!
      </h1>
      <p style={{ ...subText, textAlign: "center" }}>
        Your onboarding is live. KIRA will remind you about upcoming tasks.
      </p>

      <div style={{
        display: "grid", gridTemplateColumns: "repeat(2, 1fr)",
        gap: 10, marginTop: 8, width: "100%", maxWidth: 480,
      }}>
        <SummaryCard label="Buddy"    value={buddy?.name || "Assigned soon"} />
        <SummaryCard label="Tasks"    value={`${tasks.length} activated`} />
        <SummaryCard label="Policies" value={`${ackedCount} acknowledged`} />
        <SummaryCard label="KIRA"     value="Always available" />
      </div>
    </div>
  );

  const renderers = [renderStep1, renderStep2, renderStep3, renderStep4, renderStep5, renderStep6, renderStep7];
  const ActiveStep = renderers[step - 1] || renderStep1;

  // ─────────────────────────────────────────────────────────
  //  Compose
  // ─────────────────────────────────────────────────────────
  const closeBtn = {
    width: 28, height: 28, borderRadius: "50%",
    border: "0.5px solid #1a1a1a", background: "transparent",
    color: "#444", cursor: "pointer",
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    padding: 0, transition: "color 0.15s, border-color 0.15s",
  };

  const navBack = {
    padding: "12px 24px", background: "transparent",
    border: "0.5px solid #1a1a1a", borderRadius: 8,
    color: "#444", fontSize: 14, cursor: "pointer",
    transition: "color 0.15s, border-color 0.15s",
  };
  const navPrimary = {
    padding: "12px 28px", background: NEON, color: "#030303",
    border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600,
    cursor: "pointer",
    display: "inline-flex", alignItems: "center", gap: 8,
  };

  const dotsRow = { display: "flex", gap: 8, alignItems: "center" };
  const dotStyle = (i) => {
    const num      = i + 1;
    const active   = num === step;
    const complete = num < step;
    if (active) {
      return {
        width: 10, height: 10, borderRadius: "50%",
        background: NEON, cursor: "pointer",
        boxShadow: "0 0 10px rgba(0,255,153,0.4)",
        transition: "all 0.2s",
      };
    }
    if (complete) {
      return {
        width: 8, height: 8, borderRadius: "50%",
        background: "#1a3d2a", cursor: "pointer", transition: "all 0.2s",
      };
    }
    return {
      width: 8, height: 8, borderRadius: "50%",
      background: "#111", cursor: "pointer", transition: "all 0.2s",
    };
  };

  return (
    <div style={shell} role="dialog" aria-modal="true">
      <style>{keyframes}</style>

      <div style={inner}>
        {/* HEADER */}
        <div style={headerBar}>
          <div style={brand}>KweryAI</div>
          <div style={{ flex: 1, display: "flex", justifyContent: "center" }}>
            <StepPills />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
            <div style={{ fontSize: 13, color: "#333" }}>Step {step} of {TOTAL_STEPS}</div>
            <button
              type="button"
              onClick={handleClose}
              aria-label="Close onboarding"
              title="Close"
              style={closeBtn}
              onMouseEnter={(e) => { e.currentTarget.style.color = "#888"; e.currentTarget.style.borderColor = "#2a2a2a"; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = "#444"; e.currentTarget.style.borderColor = "#1a1a1a"; }}
            >
              <X size={14} />
            </button>
          </div>
        </div>

        {/* MAIN */}
        <div style={main}>
          <div style={innerMain}>
            {loading ? (
              <div style={{ padding: 40, textAlign: "center", color: "#666", fontSize: 14 }}>
                Loading your onboarding…
              </div>
            ) : error && !readingPolicy ? (
              <div style={{ color: "#E24B4A", display: "flex", alignItems: "center", gap: 8 }}>
                <AlertTriangle size={14} /> {error}
              </div>
            ) : (
              <ActiveStep />
            )}
          </div>

          {/* Policy slide-up panel */}
          {readingPolicy && (
            <div style={{
              position: "absolute", left: 0, right: 0, bottom: 0,
              height: "70%", background: "#050505",
              borderTop: "1px solid #1a1a1a",
              borderRadius: "16px 16px 0 0",
              padding: "24px 28px", overflow: "hidden",
              display: "flex", flexDirection: "column",
              animation: "kwery-slide-up 0.3s ease-out",
              maxWidth: 880, margin: "0 auto",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <div style={{ fontSize: 18, color: "#fff", fontWeight: 500 }}>{readingPolicy.title}</div>
                <button onClick={closePolicy}
                        style={{ background: "transparent", border: "none", color: "#666", cursor: "pointer", padding: 0, lineHeight: 0 }}>
                  <X size={18} />
                </button>
              </div>

              <div style={{ height: "0.5px", background: "#111" }} />

              <div style={{
                flex: 1, overflowY: "auto", padding: "16px 0",
                fontSize: 14, color: "#777", lineHeight: 1.8,
              }}>
                {readingPolicy.description ||
                  "No description provided. Click the file link to read the full policy."}
                {readingPolicy.file_url && (
                  <div style={{ marginTop: 10 }}>
                    <a href={readingPolicy.file_url} target="_blank" rel="noreferrer"
                       style={{ color: NEON, fontSize: 13, textDecoration: "underline" }}>
                      Open the policy document ↗
                    </a>
                  </div>
                )}
              </div>

              <div style={{
                borderTop: "0.5px solid #111", paddingTop: 16,
                display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
              }}>
                <label style={{ display: "flex", gap: 10, alignItems: "center",
                                 fontSize: 13, color: "#666", cursor: "pointer" }}>
                  <input type="checkbox" checked={ackChecked}
                         onChange={(e) => setAckChecked(e.target.checked)} />
                  I have read and understood this policy.
                </label>
                <div style={{ display: "flex", gap: 8 }}>
                  <button style={navBack} onClick={closePolicy}>Cancel</button>
                  <button
                    style={{
                      ...navPrimary, padding: "10px 20px",
                      opacity: ackChecked ? 1 : 0.4,
                      pointerEvents: ackChecked ? "auto" : "none",
                    }}
                    onClick={acknowledgePolicy}
                  >
                    Acknowledge <Check size={13} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div style={footerBar}>
          <div style={{ width: 100 }}>
            {step > 1 && (
              <button type="button" onClick={goBack}
                      style={navBack}
                      onMouseEnter={(e) => { e.currentTarget.style.color = "#888"; e.currentTarget.style.borderColor = "#2a2a2a"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = "#444"; e.currentTarget.style.borderColor = "#1a1a1a"; }}>
                ← Back
              </button>
            )}
          </div>

          <div style={dotsRow}>
            {Array.from({ length: TOTAL_STEPS }, (_, i) => (
              <div key={i}
                   role="button"
                   aria-label={`Go to step ${i + 1}`}
                   onClick={() => goTo(i + 1)}
                   style={dotStyle(i)} />
            ))}
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", width: 220 }}>
            {step === TOTAL_STEPS ? (
              <button type="button" onClick={handleFinish} style={navPrimary}>
                Go to dashboard <ArrowRight size={14} />
              </button>
            ) : step === 1 ? (
              <button type="button" onClick={goNext} style={navPrimary}>
                Let's go <ArrowRight size={14} />
              </button>
            ) : (
              <button type="button" onClick={goNext} style={navPrimary}>
                Next <ArrowRight size={14} />
              </button>
            )}
          </div>
        </div>
      </div>

      {dismissToast && (
        <div style={{
          position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
          background: "#0d0d0d", border: "0.5px solid #1a1a1a",
          borderRadius: 8, padding: "10px 16px",
          color: "#888", fontSize: 12, zIndex: 10000,
        }}>{dismissToast.text}</div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
//  Tiny sub-components used by step renderers
// ─────────────────────────────────────────────────────────
function BuddyStat({ label, value, truncate = false }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 11, color: "#333", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>{label}</div>
      <div style={{
        fontSize: 14, color: "#777",
        ...(truncate ? { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" } : {}),
      }}>{value}</div>
    </div>
  );
}

function SummaryCard({ label, value }) {
  return (
    <div style={{
      background: "#0a0a0a", border: "0.5px solid #111",
      borderRadius: 10, padding: "14px 16px", textAlign: "left",
    }}>
      <div style={{ fontSize: 14, color: "#00FF99", marginBottom: 4 }}>✓</div>
      <div style={{ fontSize: 13, color: "#fff", fontWeight: 500 }}>{label}</div>
      <div style={{ fontSize: 12, color: "#555", marginTop: 2 }}>{value}</div>
    </div>
  );
}
